<template>
	<div>
		<div class="p4">
			<!-- Select Stakeholder -->
			<div class="fw7 mb1">Select Stakeholder</div>
			<core-select-internals v-model="form.internal" />

			<!-- Select Type -->
			<div v-if="typeOptions.length > 0">
				<div class="fw7 mb1 mt6">Select Type</div>
				<j-base-select v-model="form.type" :options="typeOptions" />
			</div>

			<!-- Type -->
			<div v-else>
				<div class="fw7 mb1 mt6">Type</div>
				<j-base-input-text v-model="form.type" />
			</div>

			<j-button
				:isDisabled="!isValid"
				class="mt6"
				:isLoading="isLoading"
				@click="addStakeholder"
			>{{ $t("add") }}</j-button>
		</div>
	</div>
</template>

<script>
import CoreSelectInternals from "@/modules/core/components/CoreSelectInternals.vue"
import JBaseSelect from "@/components/JBaseSelect/"
import JBaseInputText from "@/components/JBaseInputText/"
import JButton from "@/components/JButton"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: { CoreSelectInternals, JBaseSelect, JButton, JBaseInputText },
	props: {
		caseId: String,
		proposalId: String,
		offerId: String,
		policyId: String,
		claimId: String,
		transactionId: String,

		// Check the parent for the ENUMs of stakeholdersOf
		stakeholdersOf: String,

		// Options for type of stakeholder
		typeOptions: {
			type: Array,
			required: true,
			default: () => []
		}
	},

	data() {
		return {
			isLoading: false,
			form: { internal: [], type: null }
		}
	},

	computed: {
		isValid() {
			return this.form.internal?.length && this.form.type ? true : false
		}
	},

	methods: {
		async addStakeholder() {
			// Based on stakeholdersOf, decide mutation
			if (this.stakeholdersOf === "case") this.addStakeholderToCase()
			if (this.stakeholdersOf === "proposal") this.addStakeholderToProposal()
			if (this.stakeholdersOf === "offer") this.addStakeholderToProposalOffer()
			if (this.stakeholdersOf === "policy") this.addStakeholderToPolicy()
			if (this.stakeholdersOf === "transaction")
				this.addStakeholderToTransaction()
			if (this.stakeholdersOf === "claim") this.addStakeholderToClaim()
		},

		async addStakeholderToCase() {
			const query = gql`
				mutation addStakeholderToCase(
					$caseId: String!
					$input: addStakeholderInput!
				) {
					addStakeholderToCase(caseId: $caseId, input: $input) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			const variables = {
				caseId: this.caseId,
				input: {
					entityId: this.form.internal?.[0]?.id,
					type: this.form.type
				}
			}

			await this.executeMutation({
				query,
				variables,
				mutationName: "addStakeholderToCase"
			})
		},

		async addStakeholderToProposalOffer() {
			const query = gql`
				mutation addStakeholderToProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$input: addStakeholderInput!
				) {
					addStakeholderToProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						input: $input
					) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				input: {
					entityId: this.form.internal?.[0]?.id,
					type: this.form.type
				}
			}

			await this.executeMutation({
				query,
				variables,
				mutationName: "addStakeholderToProposalOffer"
			})
		},

		async addStakeholderToProposal() {
			const query = gql`
				mutation addStakeholderToProposal(
					$caseId: String!
					$proposalId: String!
					$input: addStakeholderInput!
				) {
					addStakeholderToProposal(
						caseId: $caseId
						proposalId: $proposalId
						input: $input
					) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				input: {
					entityId: this.form.internal?.[0]?.id,
					type: this.form.type
				}
			}

			await this.executeMutation({
				query,
				variables,
				mutationName: "addStakeholderToProposal"
			})
		},

		async addStakeholderToPolicy() {
			const query = gql`
				mutation addStakeholderToPolicy(
					$policyId: String!
					$input: addStakeholderInput!
				) {
					addStakeholderToPolicy(policyId: $policyId, input: $input) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			const variables = {
				policyId: this.policyId,
				input: {
					entityId: this.form.internal?.[0]?.id,
					type: this.form.type
				}
			}

			await this.executeMutation({
				query,
				variables,
				mutationName: "addStakeholderToPolicy"
			})
		},

		// TODO: check after API schema done
		async addStakeholderToTransaction() {
			const query = gql`
				mutation addStakeholderToTransaction(
					$transactionId: String!
					$input: addStakeholderInput!
				) {
					addStakeholderToTransaction(
						transactionId: $transactionId
						input: $input
					) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			const variables = {
				transactionId: this.transactionId,
				input: {
					entityId: this.form.internal?.[0]?.id,
					type: this.form.type
				}
			}

			await this.executeMutation({
				query,
				variables,
				mutationName: "addStakeholderToTransaction"
			})
		},

		// TODO: check after API schema done
		async addStakeholderToClaim() {
			const query = gql`
				mutation addStakeholderToClaim(
					$claimId: String!
					$input: addStakeholderInput!
				) {
					addStakeholderToClaim(claimId: $claimId, input: $input) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			const variables = {
				claimId: this.claimId,
				input: {
					entityId: this.form.internal?.[0]?.id,
					type: this.form.type
				}
			}

			await this.executeMutation({
				query,
				variables,
				mutationName: "addStakeholderToClaim"
			})
		},

		/**
		 * Helper function to execute mutation call
		 * It handles errors displaying as well
		 */
		async executeMutation({ query = "", variables = {}, mutationName = "" }) {
			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false

			// Handle errors
			if (res?.data?.[mutationName]?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.[mutationName]?.errors?.[0] || this.$t("errors.general")
				)
				return Promise.resolve()
			}

			// used to update stakeholder cache in store
			const stakeholderObj = {
				id: res?.data?.[mutationName]?.createdStatus?.id,
				type: this.form?.type,
				entity: {
					name: this.form?.internal?.[0].name
				}
			}

			// Success
			this.$emit("stakeholder:added", stakeholderObj)
			return Promise.resolve()
		}
	}
}
</script>
